import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Info } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { HubAddressInput, type HubAddressValue } from '@/components/deadhead/HubAddressInput'

// Setting keys for deadhead fees
const DEADHEAD_SETTINGS = {
  ENABLED: 'deadhead_enabled',
  HUB_ADDRESS: 'deadhead_hub_address',
  HUB_LAT: 'deadhead_hub_lat',
  HUB_LNG: 'deadhead_hub_lng',
  PICKUP_MILES_INCLUDED: 'deadhead_pickup_miles_included',
  PICKUP_PRICE_PER_MILE: 'deadhead_pickup_price_per_mile',
  DROPOFF_MILES_INCLUDED: 'deadhead_dropoff_miles_included',
  DROPOFF_PRICE_PER_MILE: 'deadhead_dropoff_price_per_mile',
} as const

// Validation schema
const deadheadSchema = z.object({
  enabled: z.boolean().default(false),
  hub_address: z.string().optional(),
  hub_lat: z.coerce.number().optional(),
  hub_lng: z.coerce.number().optional(),
  pickup_miles_included: z.coerce.number().min(0, 'Must be 0 or greater').default(0),
  pickup_price_per_mile: z.coerce.number().min(0, 'Must be 0 or greater').default(0),
  dropoff_miles_included: z.coerce.number().min(0, 'Must be 0 or greater').default(0),
  dropoff_price_per_mile: z.coerce.number().min(0, 'Must be 0 or greater').default(0),
})

type DeadheadFormData = z.infer<typeof deadheadSchema>

export function DeadheadFeesPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['deadhead-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)
        .like('key', 'deadhead_%')

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form setup
  const form = useForm<DeadheadFormData>({
    resolver: zodResolver(deadheadSchema),
    defaultValues: {
      enabled: false,
      hub_address: '',
      hub_lat: 0,
      hub_lng: 0,
      pickup_miles_included: 0,
      pickup_price_per_mile: 0,
      dropoff_miles_included: 0,
      dropoff_price_per_mile: 0,
    },
  })

  // Watch enabled state for conditional rendering
  const isEnabled = form.watch('enabled')

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      form.reset({
        enabled: settings[DEADHEAD_SETTINGS.ENABLED] === 'true',
        hub_address: settings[DEADHEAD_SETTINGS.HUB_ADDRESS] || '',
        hub_lat: settings[DEADHEAD_SETTINGS.HUB_LAT]
          ? Number(settings[DEADHEAD_SETTINGS.HUB_LAT])
          : 0,
        hub_lng: settings[DEADHEAD_SETTINGS.HUB_LNG]
          ? Number(settings[DEADHEAD_SETTINGS.HUB_LNG])
          : 0,
        pickup_miles_included: settings[DEADHEAD_SETTINGS.PICKUP_MILES_INCLUDED]
          ? Number(settings[DEADHEAD_SETTINGS.PICKUP_MILES_INCLUDED])
          : 0,
        pickup_price_per_mile: settings[DEADHEAD_SETTINGS.PICKUP_PRICE_PER_MILE]
          ? Number(settings[DEADHEAD_SETTINGS.PICKUP_PRICE_PER_MILE])
          : 0,
        dropoff_miles_included: settings[DEADHEAD_SETTINGS.DROPOFF_MILES_INCLUDED]
          ? Number(settings[DEADHEAD_SETTINGS.DROPOFF_MILES_INCLUDED])
          : 0,
        dropoff_price_per_mile: settings[DEADHEAD_SETTINGS.DROPOFF_PRICE_PER_MILE]
          ? Number(settings[DEADHEAD_SETTINGS.DROPOFF_PRICE_PER_MILE])
          : 0,
      })
    }
  }, [settings, form])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: DeadheadFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      // Validate hub address when enabled
      if (data.enabled && (!data.hub_lat || !data.hub_lng)) {
        throw new Error('Please select a valid hub address from the autocomplete suggestions')
      }

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.ENABLED,
          value: String(data.enabled),
          description: 'Enable deadhead fees',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.HUB_ADDRESS,
          value: data.hub_address || '',
          description: 'Business hub address',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.HUB_LAT,
          value: String(data.hub_lat ?? 0),
          description: 'Hub latitude',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.HUB_LNG,
          value: String(data.hub_lng ?? 0),
          description: 'Hub longitude',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.PICKUP_MILES_INCLUDED,
          value: String(data.pickup_miles_included ?? 0),
          description: 'Free miles for pickup deadhead',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.PICKUP_PRICE_PER_MILE,
          value: String(data.pickup_price_per_mile ?? 0),
          description: 'Price per mile for pickup deadhead',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.DROPOFF_MILES_INCLUDED,
          value: String(data.dropoff_miles_included ?? 0),
          description: 'Free miles for dropoff deadhead',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: DEADHEAD_SETTINGS.DROPOFF_PRICE_PER_MILE,
          value: String(data.dropoff_price_per_mile ?? 0),
          description: 'Price per mile for dropoff deadhead',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deadhead-settings', activeAccount?.id] })
      toast({
        title: 'Deadhead fees saved',
        description: 'Your deadhead fee settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Handle hub address change
  const handleHubAddressChange = (value: HubAddressValue) => {
    form.setValue('hub_address', value.address)
    form.setValue('hub_lat', value.lat)
    form.setValue('hub_lng', value.lng)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
          className="space-y-6"
        >
          {/* Enable/Disable Card */}
          <Card>
            <CardHeader>
              <CardTitle>Deadhead Fees</CardTitle>
              <CardDescription>
                Charge additional fees when pickup or dropoff locations are far from your garage/office.
                This accounts for the "deadhead" mileage drivers travel without a paying passenger.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Deadhead Fees</FormLabel>
                      <FormDescription>
                        Add distance fees for pickups and dropoffs far from your hub
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Hub Address Card - only show when enabled */}
          {isEnabled && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Hub Location</CardTitle>
                  <CardDescription>
                    Your business location (garage/office). Deadhead fees are calculated based on
                    distance from this address.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="hub_address"
                    render={() => (
                      <FormItem>
                        <FormLabel>Hub Address</FormLabel>
                        <FormControl>
                          <HubAddressInput
                            value={{
                              address: form.watch('hub_address') || '',
                              lat: form.watch('hub_lat') || 0,
                              lng: form.watch('hub_lng') || 0,
                            }}
                            onChange={handleHubAddressChange}
                            placeholder="Enter your business address"
                          />
                        </FormControl>
                        <FormDescription>
                          Select your address from the autocomplete suggestions to ensure accurate coordinates.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Pickup Deadhead Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Pickup Distance Fee</CardTitle>
                  <CardDescription>
                    Fee charged when the pickup location is beyond the included miles from your hub.
                    Driver must travel to reach the customer.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="pickup_miles_included"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Miles Included (Free)</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                className="w-24"
                                {...field}
                              />
                              <span className="text-muted-foreground">miles</span>
                            </div>
                          </FormControl>
                          <FormDescription>
                            No fee for pickups within this distance
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="pickup_price_per_mile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price Per Mile</FormLabel>
                          <FormControl>
                            <CurrencyInput
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? 0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Charged for each mile beyond included
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Example: With 10 miles included and $2/mile, a pickup 25 miles from hub costs
                      (25 - 10) x $2 = $30 pickup distance fee.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Dropoff Deadhead Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Dropoff Distance Fee</CardTitle>
                  <CardDescription>
                    Fee charged when the dropoff location is far from your hub.
                    Driver must return without a passenger.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="dropoff_miles_included"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Miles Included (Free)</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                className="w-24"
                                {...field}
                              />
                              <span className="text-muted-foreground">miles</span>
                            </div>
                          </FormControl>
                          <FormDescription>
                            No fee for dropoffs within this distance
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="dropoff_price_per_mile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price Per Mile</FormLabel>
                          <FormControl>
                            <CurrencyInput
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? 0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Charged for each mile beyond included
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Example: With 10 miles included and $2/mile, a dropoff 18 miles from hub costs
                      (18 - 10) x $2 = $16 dropoff distance fee.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </>
          )}

          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </form>
      </Form>
    </div>
  )
}
